import { DataSource } from '@angular/cdk/table';
import { Component, OnInit, ViewChild, ElementRef, OnDestroy } from '@angular/core';

import { MatMenuTrigger } from '@angular/material/menu';
import { translateObject } from '@jsverse/transloco';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { toBlob } from 'html-to-image';
import moment from 'moment';

import { Observable, of } from 'rxjs';

import { FilterService, WidgetQueryOptions } from 'app/modules/collection/services/filter.service';
import { ComparisonHandlerService } from 'app/modules/dashboard/services/comparison-handler-service';
import { CustomFieldsService } from 'app/modules/field/custom-fields.service';
import { AuthService } from 'app/providers/bridge-api/auth.service';
import { StorageAccountService } from 'app/providers/storage-api/blender-account.service';
import { InteractiveSmartSummaryService } from 'app/providers/storage-api/interactive-smart-summary.service';
import { ReportService } from 'app/providers/storage-api/report.service';
import { AlertService } from 'app/services/alert.service';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';

import { TimezoneService } from 'app/services/timezone.service';
import { WidgetLimiterService } from 'app/services/widget-limiter.service';
import { SystemService } from '../../../../services/system.service';
import { AbstractWidgetComponent } from '../widget/abstract-widget.component';
import { WidgetComponent } from '../widget/widget.component';

import { WidgetChartOptions } from '../widget-chart/widget-chart.component';
import { TopMoversChartReport } from '../../services/chart-report';
import { PptxReport } from '../../services/pptx.service';
import { WidgetCommentsHandlerService } from '../../services/widget-comments-handler.service';

export interface TopMoverResult {
  featureName: string;
  intervalPercentDiff: number;
  longIntervalPercentDiff: number;
  featureDocumentHits: number;
  intervalSentimentDiff: number;
  longIntervalSentimentDiff: number;
}

class TopMoverDataSource extends DataSource<any> {
  public topMovers: TopMoverResult[] = [];

  public displayedColumns = [
    'featureName',
    'intervalPercentDiff',
    'intervalSentimentDiff',
    'featureDocumentHits',
    'longIntervalPercentDiff',
    'longIntervalSentimentDiff',
  ];

  constructor() {
    super();
  }

  public connect(): Observable<TopMoverResult[]> {
    return of(this.topMovers);
  }

  public disconnect(): void {}

  public async loadTopMovers(topMovers: TopMoverResult[]): Promise<void> {
    topMovers = topMovers.sort(
      (a, b) => Math.abs(b.intervalPercentDiff) - Math.abs(a.intervalPercentDiff)
    );
    this.topMovers = topMovers;
  }
}

export interface TopMoversWidgetOptions extends WidgetChartOptions {
  selectedDate?: Date;
  minimumOccurrences?: number;
}

@EnableClassTelemetry()
@Component({
  selector: 'app-widget-top-movers',
  templateUrl: './widget-top-movers.component.html',
  styleUrls: ['./widget-top-movers.component.scss'],
  standalone: false,
})
export class WidgetTopMoversComponent
  extends AbstractWidgetComponent<TopMoversWidgetOptions>
  implements OnInit, OnDestroy
{
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  public dataSource: TopMoverDataSource;
  public report: TopMoversChartReport;
  public downloadFormats = ['csv', 'pptx', 'image'];
  public dateIntervals = ['day', 'week', 'month', 'year'];
  public modelDateTopFrom: Date;
  canFilter = true;
  hasAnalyses = true;
  public minimumOccurences: number;

  @ViewChild(MatMenuTrigger)
  contextMenuTriggerElement: MatMenuTrigger;
  @ViewChild(MatMenuTrigger, { read: ElementRef })
  contextMenuTriggerNativeElement: ElementRef;

  constructor(
    private widgetLimiterService: WidgetLimiterService,
    private comparisonService: ComparisonHandlerService,
    private reportService: ReportService,
    protected systemService: SystemService,
    protected accountService: StorageAccountService,
    protected authService: AuthService,
    protected timezoneService: TimezoneService,
    protected filterService: FilterService,
    protected alertService: AlertService,
    protected interactiveSmartSummaryService: InteractiveSmartSummaryService,
    protected widgetComponent: WidgetComponent,
    protected customFieldService: CustomFieldsService,
    protected commentsHandlerService: WidgetCommentsHandlerService
  ) {
    super(
      systemService,
      accountService,
      authService,
      timezoneService,
      filterService,
      alertService,
      interactiveSmartSummaryService,
      widgetComponent,
      customFieldService,
      commentsHandlerService
    );
  }

  public get widgetQueryOptions(): WidgetQueryOptions {
    const widgetQueryOptions = this.filterService.getWidgetQueryOptions(
      this.widget,
      this.widgetDataSource,
      this.widgetGroup
    );
    widgetQueryOptions.analyticItems = ['documents', 'topics', 'entities', 'phrases', 'themes'];
    return widgetQueryOptions;
  }

  public async loadTopMovers() {
    const queryOptions = this.widgetQueryOptions;
    //Since we are getting all of the values in the terms aggregations limit the buckets we'll be searching.
    queryOptions.filters.dates.and.push(this.dateLimitFilter(this.widget.payload.dateInterval));

    const queryForDocuments = this.filterService.getQueryForDocuments(queryOptions, false, true);
    const dateOffset = this.getDateOffset();
    const currentDate = this.widget.payload.selectedDate
      ? new Date(new Date(this.widget.payload.selectedDate).setHours(0, 0, 0, 0))
      : new Date(new Date().setHours(0, 0, 0, 0));
    const selectedDateString =
      currentDate.getFullYear() +
      ('0' + (currentDate.getMonth() + 1)).slice(-2) +
      ('0' + currentDate.getDate()).slice(-2);

    const report = await this.report.executeReportForTopMovers(
      queryForDocuments,
      dateOffset,
      selectedDateString,
      this.widget.payload.minimumOccurrences
    );
    this.filteredDocumentCount = this.report.lastDocumentCount;

    if (this.widgetLoadController.signal.aborted) {
      return;
    }

    this.dataSource.topMovers = report;
    this.dataSource.loadTopMovers(report);
  }

  //This is limiting the data we'll search for in elastic to the ranges needed for the report.
  private dateLimitFilter(dateInterval: string) {
    //For the day we want the range to go to the end of the day.
    //For others we will manually set the forward range to a future date so we don't want to go to the end
    //of the selected day.
    const hourOffset = dateInterval === 'day' ? 24 : 0;
    const currentDate = this.widget.payload.selectedDate
      ? new Date(new Date(this.widget.payload.selectedDate).setHours(hourOffset, 0, 0, 0))
      : new Date(new Date(this.widgetDataSource.maxDocAt).setHours(hourOffset, 0, 0, 0));
    const startDate = new Date(new Date(currentDate).setHours(0, 0, 0, 0));
    switch (dateInterval) {
      case 'day':
        startDate.setDate(startDate.getDate() - 31);
        break;
      case 'week':
        startDate.setDate(startDate.getDate() - 31);
        //Project forward a week
        if (this.widget.payload.selectedDate) {
          currentDate.setDate(currentDate.getDate() + 7);
        }
        break;
      case 'month':
        startDate.setDate(startDate.getDate() - 186);
        //Project the number of days in the month forward if there is a selected date.
        if (this.widget.payload.selectedDate) {
          currentDate.setDate(
            currentDate.getDate() +
              new Date(currentDate.getFullYear(), startDate.getMonth(), 0).getDate()
          );
        }
        break;
      case 'year':
        startDate.setDate(startDate.getDate() - 1095);
        //Project foward a year
        if (this.widget.payload.selectedDate) {
          currentDate.setDate(currentDate.getDate() + 365);
        }
        break;
      default:
        startDate.setDate(startDate.getDate() - 31);
        break;
    }
    const dateValue = `daterange/${startDate.toISOString()}/${currentDate.toISOString()}`;
    return {
      key: '24h',
      analyticItem: 'createdAt',
      value: dateValue,
    };
  }

  //Based on the selected date, form the bucket offset.
  private getDateOffset() {
    if (this.widget.payload.dateInterval === 'week' && this.widget.payload.selectedDate) {
      return this.widget.payload.selectedDate.getDay() - 1;
    }
    if (this.widget.payload.dateInterval === 'month' && this.widget.payload.selectedDate) {
      return this.widget.payload.selectedDate.getDate() - 1;
    }
    if (this.widget.payload.dateInterval === 'year' && this.widget.payload.selectedDate) {
      const timestmp = new Date().setFullYear(this.widget.payload.selectedDate.getFullYear(), 0, 1);
      const selectedDateYearFirstDay = Math.floor(timestmp / 86400000);
      const selectedDateDay = Math.ceil(this.widget.payload.selectedDate.getTime() / 86400000);
      return selectedDateDay - selectedDateYearFirstDay;
    }
    return 0;
  }

  ngOnInit() {
    super.ngOnInit();
    super.updateFilterList();
    this.report = new TopMoversChartReport(
      this.widget.payload,
      this.reportService,
      this.filterService,
      this.comparisonService,
      this.timezoneService
    );
    this.dataSource = new TopMoverDataSource();
    this.refresh(false, true);
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.widgetLoadController.abort('destroyed');
  }

  get defaultTitle(): string {
    return 'Top Movers: ' + this.widget.payload.analyticItem;
  }

  async updateSelectedDate(date: Date) {
    this.modelDateTopFrom = date;
    this.widget.payload.selectedDate = this.modelDateTopFrom;
    this.dashboardStore.markDirty();
    await this.refresh();
  }

  async refresh(_reset = false, initialLoad = false) {
    this.isLoading = true;
    if (this.dataSourceNotFound) {
      this.loadingStarted.emit(initialLoad);
      this.loadingFinished.emit(initialLoad);
      this.isLoading = false;
      return;
    }

    this.loadingStarted.emit(initialLoad);
    if (!this.widget.payload.selectedDate) {
      this.widget.payload.selectedDate = new Date(
        new Date(this.widgetDataSource.maxDocAt).setHours(0, 0, 0, 0)
      );
    } else {
      this.widget.payload.selectedDate = new Date(
        new Date(this.widget.payload.selectedDate).setHours(0, 0, 0, 0)
      );
    }
    if (!this.widget.payload.minimumOccurrences) {
      this.widget.payload.minimumOccurrences = 1;
    }
    this.minimumOccurences = this.widget.payload.minimumOccurrences;
    this.modelDateTopFrom = new Date(this.widget.payload.selectedDate);
    await this.widgetLimiterService.execute(async () => {
      if (this.widgetLoadController.signal.aborted) {
        return;
      }

      await this.loadTopMovers();
    });
    this.isLoading = false;
    this.loadingFinished.emit(initialLoad);
  }

  public query(): string {
    return this.filterService.getQueryForDocuments(this.widgetQueryOptions, false, true);
  }

  public arrowIcon(value: number) {
    if (value > 0) {
      return 'arrow_upward';
    }
    if (value < 0) {
      return 'arrow_downward';
    }
    return undefined;
  }

  public negative(value: number) {
    return value < 0;
  }

  public positive(value: number) {
    return value > 0;
  }

  async exportAsImage(): Promise<any> {
    const tableWrapper = document
      .getElementsByClassName('top-mover-table-wrapper')
      .item(0) as HTMLElement;

    const currentScrollLeft = tableWrapper.scrollLeft;
    const currentScrollTop = tableWrapper.scrollTop;
    tableWrapper.scroll(0, 0);

    const table = document.getElementsByClassName('top-mover-table').item(0) as HTMLElement;

    return toBlob(table).then((blob) => {
      tableWrapper.scroll(currentScrollLeft, currentScrollTop);
      return blob;
    });
  }

  async exportAsPptx(pptx: PptxReport): Promise<void> {
    const widgetTitle =
      !this.widget.title && this.widget.title !== '' ? this.widget.title : this.defaultTitle;
    const translations = translateObject('widgets.pptx');
    const rows = [
      [{ text: translations['widget-name'] }, { text: widgetTitle }],
      [{ text: translations['dashboard-name'] }, { text: this.dashboard.title }],
      [{ text: translations['datasource-name'] }, { text: this.widgetDataSource.displayName }],
      [{ text: translations['analysis-id'] }, { text: this.widgetDataSource.analysisId }],
      [{ text: translations['group-id'] }, { text: this.widgetDataSource.groupId }],
      [{ text: translations['generated'] }, { text: moment().format('LLL') }],
    ];

    pptx.addTableSlide(rows);

    const dataRows = [
      { text: translations['mover-name'] },
      { text: translations['interval-percentage-change'] },
      { text: translations['interval-sentiment-change'] },
      { text: translations['mover-document-hits'] },
      { text: translations['secondary-interval-percentage-change'] },
      { text: translations['secondary-interval-sentiment-change'] },
      ,
    ];
    for (const mover of this.dataSource.topMovers) {
      dataRows.push(
        { text: mover.featureName },
        { text: mover.intervalPercentDiff.toString() },
        { text: mover.longIntervalPercentDiff.toString() },
        { text: mover.featureDocumentHits.toString() },
        { text: mover.intervalSentimentDiff.toFixed(2).toString() },
        { text: mover.longIntervalSentimentDiff.toFixed(2).toString() }
      );
    }
    if (this.report.canExportAsPptx) {
      pptx.addTableSlide(dataRows);
    }
  }

  public isDateIntervalEnabled(_dateInterval) {
    return true;
  }

  public getDateIntervals() {
    return this.dateIntervals;
  }

  public openMinimumOccurrencesPopover(popover: NgbPopover, $event?) {
    popover.open({ $implicit: popover });
    if ($event) {
      $event.stopImmediatePropagation();
    }
  }

  public closeMinimumOccurrencesPopover($event: number, popover: NgbPopover) {
    this.widget.payload.minimumOccurrences = $event;
    this.minimumOccurences = $event;
    popover.close();
    this.dashboardStore.markDirty();
    this.refresh();
  }

  public canShowDataTable() {
    return false;
  }
}
